import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { LcUtilizationRow } from './logic';

/**
 * GWI 360 adoption per LC — the picture the Home dashboard's GWI 360
 * Utilization card drew for dispatchers (retired 2026-09-16), without its
 * period-over-period segment: one 100% bar per LC, blue = share of their saves
 * made in GWI 360, green = V1, highest share first. Backed by the per-LC-per-day
 * rows of /reporting/dispatcher-metrics?sections=utilization (cms.history saves
 * over the page's date range), summed here. An LC under 100% is a link: their
 * name (or their green segment) opens the V1 saves drill-down.
 */

export interface AdoptionBar {
  key: string;
  name: string;
  gwi360: number;  // saves made in GWI 360 over the window
  v1: number;      // saves made in V1 over the window
  pct: number;     // GWI 360 share, 0–100, rounded
}

/** One bar per LC with saves, highest GWI 360 share first (ties by volume). */
export function buildAdoptionBars(rows: LcUtilizationRow[]): AdoptionBar[] {
  const acc = new Map<string, { name: string; gwi360: number; v1: number }>();
  for (const r of rows) {
    const a = acc.get(r.dispatcher_key) ?? { name: r.dispatcher, gwi360: 0, v1: 0 };
    a.gwi360 += r.gwi360_count;
    a.v1 += r.v1_count;
    acc.set(r.dispatcher_key, a);
  }
  return Array.from(acc.entries())
    .filter(([, a]) => a.gwi360 + a.v1 > 0)
    .map(([key, a]) => ({ key, ...a, pct: Math.round((a.gwi360 / (a.gwi360 + a.v1)) * 100) }))
    .sort((a, b) => b.pct - a.pct || (b.gwi360 + b.v1) - (a.gwi360 + a.v1));
}

const BLUE = '#3b82f6';
const GREEN = '#10b981';

// Text follows the user's Display Preferences font size (the app's root font
// size, `rootFontPx`) rather than a fixed pixel size — SVG text ignores the
// page's CSS scale unless told. Names are the largest, bar labels the smallest.
const NAME_FONT = 0.8;   // rem
const AXIS_FONT = 0.7;
const LABEL_FONT = 0.65;
// Name column wide enough for the longest roster name ("Stephanie Brooks
// (Shared-Archive)") at NAME_FONT, scaled with the root size.
const NAME_COLUMN_EM = 15.5;

interface Props {
  rows: LcUtilizationRow[];
  isDark: boolean;
  rootFontPx: number;  // the app's current root font size, from Display Preferences
  onLcClick?: (bar: AdoptionBar) => void;  // offered for LCs with any V1 saves
}

const LcAdoptionChart: React.FC<Props> = ({ rows, isDark, rootFontPx, onLcClick }) => {
  const bars = useMemo(() => buildAdoptionBars(rows), [rows]);
  const data = useMemo(() => bars.map((b) => ({ name: b.name, 'GWI 360': b.pct, 'V1': 100 - b.pct, _bar: b })), [bars]);
  const byName = useMemo(() => new Map(bars.map((b) => [b.name, b])), [bars]);
  const clickable = (b: AdoptionBar | undefined): b is AdoptionBar => !!onLcClick && !!b && b.v1 > 0;
  const openLc = (b: AdoptionBar | undefined) => { if (clickable(b)) onLcClick!(b); };

  if (bars.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">
        No saves in this window.
      </div>
    );
  }

  const tick = isDark ? '#d1d5db' : '#666';
  const link = isDark ? '#60a5fa' : '#2563eb';
  const rowPx = Math.round(rootFontPx * NAME_FONT * 2.3);  // room for the name plus a gap
  const barHeight = Math.max(320, bars.length * rowPx);
  const nameColumnPx = Math.round(rootFontPx * NAME_FONT * NAME_COLUMN_EM);
  const labelFont = `${LABEL_FONT}rem`;

  return (
    <div className="flex flex-col min-h-0">
      <div className="overflow-auto">
        <ResponsiveContainer width="100%" height={barHeight}>
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? '#374151' : '#e5e7eb'} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={(props: any) => (
                <text x={props.x} y={props.y} dy={12} textAnchor="middle" style={{ fontSize: `${AXIS_FONT}rem` }} fill={tick}>
                  {`${props.payload.value}%`}
                </text>
              )}
            />
            {/* Names on one line: a fixed column wide enough for the longest LC
                name, and a plain <text> tick so Recharts never wraps it. LCs with
                V1 saves render as links into the drill-down. */}
            <YAxis
              type="category"
              dataKey="name"
              width={nameColumnPx}
              interval={0}
              tick={(props: any) => {
                const name = props.payload.value as string;
                const bar = byName.get(name);
                const isLink = clickable(bar);
                return (
                  <text
                    x={props.x - 6}
                    y={props.y}
                    dy={rootFontPx * NAME_FONT * 0.35}
                    textAnchor="end"
                    style={{ fontSize: `${NAME_FONT}rem`, cursor: isLink ? 'pointer' : undefined }}
                    fill={isLink ? link : tick}
                    textDecoration={isLink ? 'underline' : undefined}
                    onClick={isLink ? () => openLc(bar) : undefined}
                    role={isLink ? 'button' : undefined}
                  >
                    {isLink && <title>{`Show ${name}'s V1 saves`}</title>}
                    {name}
                  </text>
                );
              }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: 'rgb(31 41 55)', border: 'none', borderRadius: '0.5rem' }}
              itemStyle={{ color: '#fff' }}
              labelStyle={{ color: '#fff' }}
              formatter={((value: number, name: string, props: any) => {
                const b: AdoptionBar = props.payload._bar;
                if (name === 'GWI 360') return [`${b.pct}% (${b.gwi360} of ${b.gwi360 + b.v1} saves)`, 'GWI 360'];
                if (name === 'V1') return [`${100 - b.pct}% (${b.v1} saves)`, 'V1'];
                return [`${value}%`, name];
              }) as any}
            />
            <Bar dataKey="GWI 360" stackId="a" fill={BLUE} isAnimationActive={false}>
              <LabelList
                content={({ x, y, width, height, index }: any) => {
                  const b = bars[index];
                  if (!b || b.pct === 0) return null;
                  const cy = (y as number) + (height as number) / 2;
                  // A sliver of blue can't hold its label: set it just past the
                  // segment instead (it lands on the green, still white on colour).
                  if ((width as number) < rootFontPx * 2) {
                    return <text key={index} x={(x as number) + (width as number) + 4} y={cy} textAnchor="start" dominantBaseline="middle" fill="#fff" style={{ fontSize: labelFont }}>{`${b.pct}%`}</text>;
                  }
                  const cx = (x as number) + (width as number) / 2;
                  return <text key={index} x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill="#fff" style={{ fontSize: labelFont }}>{`${b.pct}%`}</text>;
                }}
              />
            </Bar>
            <Bar
              dataKey="V1"
              stackId="a"
              fill={GREEN}
              isAnimationActive={false}
              style={{ cursor: onLcClick ? 'pointer' : undefined }}
              onClick={(_: any, index: number) => openLc(bars[index])}
            >
              <LabelList
                content={({ x, y, width, height, index }: any) => {
                  const b = bars[index];
                  if (!b || b.pct !== 0) return null;
                  const cx = (x as number) + (width as number) / 2;
                  const cy = (y as number) + (height as number) / 2;
                  return <text key={index} x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill="#fff" style={{ fontSize: labelFont }}>0%</text>;
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-label" aria-label="Legend">
        {[['GWI 360', BLUE], ['V1', GREEN]].map(([label, color]) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default LcAdoptionChart;
